import { useState } from 'react';

import { Button } from '@components/button';
import { ButtonGroup } from '@components/button-group';
import { Card } from '@components/card';
import { createForm, useCreateFormStore } from '@components/form';
import { IconEdit } from '@components/icons/edit';
import { IconEmail } from '@components/icons/email';
import { IconFormIndicatorError } from '@components/icons/form-indicator-error';
import { IconFormIndicatorInfo } from '@components/icons/form-indicator-info';
import { IconFormIndicatorSuccess } from '@components/icons/form-indicator-success';
import { IconOutput } from '@components/icons/output';
import { Sidebar, hideSidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import {
  useCreateSmtpDomain,
  useInvalidateEmailDomains,
  useUpdateSmtpDomain,
  useVerifyEmailSettings,
} from 'api/company/hooks';

type UnmanagedFormProps = {
  shortId?: string;
  smtpHost?: string;
  smtpPort?: string;
  smtpLogin?: string;
  isVerified?: boolean;
};

export const useUnmanagedEmailDomainFormSidebar = () =>
  useSidebar(UnmanagedEmailDomainForm, { variant: 'REGULAR' });

type FormValues = {
  smtpHost: string;
  smtpPort: string;
  smtpLogin: string;
  smtpPassword: string;
};

const Form = createForm<FormValues>();

const UnmanagedEmailDomainForm = ({
  shortId,
  smtpHost,
  smtpPort,
  smtpLogin,
  isVerified: isCurrentlyVerified,
}: UnmanagedFormProps) => {
  const { t } = useScopedTranslate(locale);
  const createMutation = useCreateSmtpDomain();
  const updateMutation = useUpdateSmtpDomain();
  const verifyMutation = useVerifyEmailSettings();
  const { invalidate } = useInvalidateEmailDomains();
  const isEditing = Boolean(shortId);
  const formStore = useCreateFormStore();
  const values = formStore.useFormValues();
  const [isVerified, setIsVerified] = useState<boolean | undefined>(isCurrentlyVerified);

  const submit = async (formValues: FormValues) => {
    try {
      if (isEditing && shortId) {
        await updateMutation.mutateAsync({
          shortId,
          data: {
            smtpHost: formValues.smtpHost,
            smtpPort: parseInt(formValues.smtpPort, 10),
            smtpLogin: formValues.smtpLogin,
            smtpPassword: formValues.smtpPassword || undefined,
          },
        });
      } else {
        await createMutation.mutateAsync({
          smtpHost: formValues.smtpHost,
          smtpPort: parseInt(formValues.smtpPort, 10),
          smtpLogin: formValues.smtpLogin,
          smtpPassword: formValues.smtpPassword,
        });
      }
      await invalidate();
      hideSidebar();
    } catch {
      // Error is handled by react-query
    }
  };

  const handleTestRequest = async () => {
    const response = await verifyMutation.mutateAsync({
      smtpHost: values.smtpHost as string,
      smtpPort: parseInt(values.smtpPort as string, 10),
      smtpLogin: values.smtpLogin as string,
      smtpPassword: values.smtpPassword as string,
    });
    setIsVerified(response.isVerified);
  };

  const isDirty = formStore.useIsDirty();
  const isNotEmpty = Boolean(
    values.smtpHost && values.smtpPort && values.smtpLogin && values.smtpPassword
  );

  const isLoading = createMutation.isLoading || updateMutation.isLoading;

  return (
    <Sidebar>
      <Sidebar.SimpleHeader
        icon={isEditing ? IconEdit : IconEmail}
        iconColor="elementBlue100"
        title={isEditing ? t('editEmailDomain') : t('addEmailDomain')}
        subtitle={t('subtitle')}
      />
      <Sidebar.Content>
        <Form onSubmit={submit} store={formStore}>
          <Stack space={6}>
            <Text color="textSecondary" font="regular14" text={t('description')} />
            <Stack space={4}>
              <Form.TextField
                id="smtpHost"
                label={t('emailSettingsSmtpHost')}
                validators={['required']}
                placeholder={t('smtpHostPlaceholder')}
                defaultValue={smtpHost}
                onChange={(value) =>
                  setIsVerified(value !== smtpHost ? undefined : isCurrentlyVerified)
                }
              />
              <Form.TextField
                id="smtpPort"
                label={t('emailSettingsSmtpPort')}
                validators={['required', 'number']}
                placeholder={t('smtpPortPlaceholder')}
                defaultValue={smtpPort ?? '587'}
                onChange={(value) =>
                  setIsVerified(value !== smtpPort ? undefined : isCurrentlyVerified)
                }
              />
              <Form.TextField
                id="smtpLogin"
                label={t('emailSettingsSmtpLogin')}
                validators={['required']}
                placeholder={t('smtpLoginPlaceholder')}
                defaultValue={smtpLogin}
                onChange={(value) =>
                  setIsVerified(value !== smtpLogin ? undefined : isCurrentlyVerified)
                }
              />
              <Form.PasswordField
                id="smtpPassword"
                label={
                  isEditing ? t('emailSettingsSmtpPasswordEditing') : t('emailSettingsSmtpPassword')
                }
                validators={isEditing ? [] : ['required']}
                placeholder={t('smtpPasswordPlaceholder')}
                onChange={(value) => setIsVerified(value !== '' ? undefined : isCurrentlyVerified)}
              />
              <Card space={4}>
                <Stack space={4}>
                  <div>
                    <Text font="semibold14" text={t('checkCredentials')} />
                    <Text font="regular14" text={t('checkCredentialsDescription')} />
                  </div>
                  {!isVerified && isDirty && (
                    <ButtonGroup align="left">
                      <Button
                        iconLeft={<IconOutput />}
                        text={t('testCredentials')}
                        variant="primary-blue"
                        onClick={handleTestRequest}
                        isDisabled={verifyMutation.isLoading || !isNotEmpty}
                      />
                    </ButtonGroup>
                  )}
                  <Stack orientation="horizontal" align="center" space={2}>
                    <VerifiedStatus isVerified={isVerified} />
                  </Stack>
                </Stack>
              </Card>
            </Stack>
          </Stack>
          <Sidebar.Footer>
            <Form.Actions>
              <Button
                text={t('core.cancel')}
                isDisabled={isLoading}
                onClick={hideSidebar}
                variant="secondary"
              />
              <Form.Submit
                text={isEditing ? t('core.save') : t('addDomain')}
                variant="primary-blue"
                isDisabled={isLoading || !isDirty}
              />
            </Form.Actions>
          </Sidebar.Footer>
        </Form>
      </Sidebar.Content>
    </Sidebar>
  );
};

const VerifiedStatus = ({ isVerified }: { isVerified: boolean | undefined }) => {
  const { t } = useScopedTranslate(locale);
  switch (isVerified) {
    case true:
      return (
        <>
          <IconFormIndicatorSuccess />
          <Text color="green100" font="regular14" text={t('verifiedCredentials')} />
        </>
      );
    case false:
      return (
        <>
          <IconFormIndicatorError />
          <Text color="elementRed100" font="regular14" text={t('invalidCredentials')} />
        </>
      );
    case undefined:
    default:
      return (
        <>
          <IconFormIndicatorInfo color="indigo100" />
          <Text color="indigo100" font="regular14" text={t('notVerifiedCredentials')} />
        </>
      );
  }
};

const locale = {
  en: {
    addEmailDomain: 'Add email domain',
    editEmailDomain: 'Edit email domain',
    subtitle: '(SMTP)',
    emailSettingsSmtpHost: 'Host',
    emailSettingsSmtpPort: 'Port',
    emailSettingsSmtpLogin: 'Login',
    emailSettingsSmtpPassword: 'Password',
    emailSettingsSmtpPasswordEditing: 'Password (leave blank to keep current password)',
    smtpHostPlaceholder: 'smtp.acme.com...',
    smtpPortPlaceholder: '587',
    smtpLoginPlaceholder: 'Usually an email address...',
    smtpPasswordPlaceholder: 'Password',
    checkCredentials: 'Check credentials',
    checkCredentialsDescription: 'Press the Test credentials button below to test your credentials',
    testCredentials: 'Test credentials',
    notVerifiedCredentials: 'Credentials not verified',
    verifiedCredentials: 'Credentials verified',
    invalidCredentials: 'Credentials invalid',
    addDomain: 'Add domain',
    description: 'Please refer to your email provider for the SMTP settings',
  },
};
